import React, { useEffect, useState } from "react";
import { Droplets } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { dms, inr, niceDate, statusPill } from "./api";
import { ReturnRequestButton } from "./ReturnComponents";
import { ReturnDetailDialog } from "./ReturnDetail";
import { PageHeader, EmptyState, StockLedgerSection } from "./OwnerPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, Package, Warehouse, TrendingUp, ScrollText, CheckCircle2, ChevronRight, Plus, Minus, Receipt, ClipboardList, Truck, Paperclip, Trash2, Printer, Edit, Calendar, Wallet, AlertTriangle, Users, PackageX, Store, BarChart3, RotateCcw } from "lucide-react";

// ============================================================================
// Distributor Dashboard
// ============================================================================
function ProfileIncompleteBanner() {
  const [missing, setMissing] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    dms.getMyProfile().then(p => {
      const filled = (v) => String(v || "").trim().length > 0;
      const m = [];
      if (!filled(p.address)) m.push("Address");
      if (!filled(p.pan)) m.push("PAN");
      if (!filled(p.bank_name) || !filled(p.bank_account) || !filled(p.bank_ifsc)) m.push("Bank details");
      if (!(p.documents || []).length) m.push("Documents");
      setMissing(m);
    }).catch(() => {});
  }, []);
  if (!missing.length) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="profile-incomplete-banner">
      <div className="flex items-start gap-2 text-amber-900">
        <AlertTriangle size={18} className="mt-0.5 shrink-0" />
        <div>
          <div className="text-sm font-semibold">Profile incomplete</div>
          <div className="text-xs">Please add: {missing.join(", ")}</div>
        </div>
      </div>
      <Button onClick={() => nav("/dms/my-profile")} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="complete-profile-btn">Complete Profile</Button>
    </div>
  );
}

export function DistributorDashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    dms.distributorDashboard().then(d => setKpis(d.kpis)).catch(() => {});
    dms.listOrders().then(d => setRecent((d.data || []).slice(0, 5))).catch(() => {});
  }, []);
  const [saleLiters, setSaleLiters] = useState(null);
  useEffect(() => {
    dms.distSaleLiters({}).then(d => setSaleLiters(d.total_liters ?? 0)).catch(() => {});
  }, []);
  const _now = new Date();
  const _monthStart = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-01`;
  const _today = _now.toISOString().slice(0, 10);
  const _mtdLedgerLink = `/dms/distributor/sec-ledger?date_from=${_monthStart}&date_to=${_today}`;
  const cards = [
    { label: "Current Stock",            value: kpis ? `${Number(kpis.stock_pieces ?? 0).toLocaleString("en-IN")} pcs` : "—",  icon: Warehouse,    color: "teal",    to: "/dms/distributor/stock" },
    { label: "Stock Value",              value: kpis ? inr(kpis.stock_value) : "—",       icon: Package,      color: "indigo",  to: "/dms/distributor/stock" },
    { label: "Payable to Owner",         value: kpis ? inr(kpis.payable_to_owner) : "—",  icon: ScrollText,   color: "rose",    to: "/dms/distributor/ledger" },
    { label: "Pending Primary Orders",   value: kpis?.pending_primary_orders ?? "—",       icon: ShoppingCart, color: "amber",   to: "/dms/distributor/my-orders" },
    { label: "Retailer Orders",          value: kpis?.retailer_orders_count ?? "—",       icon: ClipboardList, color: "blue",    to: "/dms/distributor/retail-orders" },
    { label: "Sales MTD (incl. GST)",                value: kpis ? inr(kpis.revenue_mtd) : "—",       icon: TrendingUp,   color: "emerald", to: "/dms/distributor/sec-ledger" },
    { label: "Today's Order",            value: kpis?.orders_today ?? "—",                icon: Calendar,     color: "blue",    to: "/dms/distributor/retail-orders" },
    { label: "Today's Order Value",      value: kpis ? inr(kpis.orders_value_today) : "—", icon: Wallet,      color: "emerald", to: "/dms/distributor/retail-orders" },
    { label: "Pending Order",            value: kpis?.pending_retail_orders ?? "—",       icon: ClipboardList, color: "amber",   to: "/dms/distributor/retail-orders" },
    { label: "Today's Invoice Value (incl. GST)",    value: kpis ? inr(kpis.invoice_value_today) : "—", icon: Receipt,    color: "indigo",  to: "/dms/distributor/sec-ledger" },
    { label: "Today's Collection",       value: kpis ? inr(kpis.collection_today) : "—",  icon: Wallet,       color: "teal",    to: "/dms/distributor/collections" },
    { label: "Monthly Sale (incl. GST)",             value: kpis ? inr(kpis.monthly_sale) : "—",      icon: TrendingUp,   color: "emerald", to: _mtdLedgerLink },
    { label: "Monthly Sale (excl. GST)",             value: kpis ? inr(kpis.monthly_sale_excl_gst || 0) : "—",      icon: TrendingUp,   color: "emerald", to: _mtdLedgerLink },
    { label: "Monthly Collection",       value: kpis ? inr(kpis.monthly_collection) : "—", icon: Wallet,      color: "blue",    to: "/dms/distributor/sec-ledger" },
    { label: "Outstanding",              value: kpis ? inr(kpis.outstanding_receivable) : "—", icon: AlertTriangle, color: "rose", to: "/dms/distributor/sec-ledger" },
    { label: "Fulfillment",              value: kpis != null ? `${kpis.fulfillment_pct}%` : "—", icon: CheckCircle2, color: "teal", to: "/dms/distributor/retail-orders" },
    { label: "Assigned Sale Person",     value: kpis?.assigned_salesperson_count ?? "—",  icon: Users,        color: "indigo",  to: "/dms/distributor/salespersons" },
    { label: "Stock Alert",              value: kpis?.stock_alert_count ?? "—",           icon: PackageX,     color: "rose",    to: "/dms/distributor/stock-alerts" },
    { label: "Total Retailer",           value: kpis?.total_retailers ?? "—",             icon: Store,        color: "amber",   to: "/dms/distributor/retailers" },
    { label: "Reports",                  value: "View",                                   icon: BarChart3,    color: "blue",    to: "/dms/reports" },
  ];
  cards.splice(13, 0, {
    label: "Sale in Liters",
    value: saleLiters != null ? `${Number(saleLiters).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L` : "—",
    icon: Droplets, color: "blue", to: "/dms/distributor/sale-liters",
  });
  const colorMap = {
    teal: "bg-[#faf6e6] text-[#a67c00]", indigo: "bg-indigo-50 text-indigo-700",
    amber: "bg-amber-50 text-amber-700", blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700", rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div>
      <PageHeader title="Distributor Dashboard" subtitle="Your business at a glance"
        action={<div className="flex flex-wrap items-center gap-2"><Button onClick={() => nav("/dms/distributor/browse")} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="place-order-cta"><ShoppingCart size={16} className="mr-1" /> Place New Order</Button></div>}
      />
      <ProfileIncompleteBanner />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {cards.map(c => (
          <Card key={c.label} className="p-4 cursor-pointer hover:shadow-md transition"
                onClick={() => nav(c.to)} data-testid={`dist-kpi-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${colorMap[c.color]}`}><c.icon size={18} /></div>
            <div className="mt-3 text-xs text-slate-500 uppercase tracking-wider">{c.label}</div>
            <div className="mt-1 text-xl font-bold text-slate-900">{c.value}</div>
          </Card>
        ))}
      </div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-900">Recent Orders</h3>
        <button onClick={() => nav("/dms/distributor/my-orders")} className="text-sm text-[#a67c00] hover:underline">View all →</button>
      </div>
      <Card>
        {recent.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">No orders yet</div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Order #</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Fulfillment</TableHead><TableHead>Status</TableHead><TableHead>Placed</TableHead></TableRow></TableHeader>
            <TableBody>
              {recent.map(o => (
                <TableRow key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/dms/distributor/my-orders/${o.id}`)}>
                  <TableCell className="font-mono text-sm">{o.order_no}</TableCell>
                  <TableCell>{o.items.length}</TableCell>
                  <TableCell className="font-medium">{inr(o.total)}</TableCell>
                  <TableCell><div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-[#faf6e6]0" style={{ width: `${o.fulfillment_pct}%` }} /></div><span className="text-xs">{o.fulfillment_pct}%</span></div></TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded-full border ${statusPill(o.status)}`}>{o.status.replace(/_/g, " ")}</span></TableCell>
                  <TableCell className="text-xs text-slate-500">{niceDate(o.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// Distributor — Browse & Place Order
// ============================================================================
export function DistributorBrowsePage() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});  // { product_id: qty }
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => { dms.browseProducts().then(d => setProducts(d.data)).catch(() => {}); }, []);

  const setQty = (pid, delta) => setCart(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 0) + delta) }));
  const setQtyDirect = (pid, val) => setCart(prev => ({ ...prev, [pid]: Math.max(0, Number(val) || 0) }));

  // All price maths use unit_price (new price). previous_price is display-only.
  const subtotal = products.reduce((s, p) => s + (cart[p.id] || 0) * p.unit_price, 0);
  const gstTotal = products.reduce((s, p) => s + (cart[p.id] || 0) * p.unit_price * (p.gst_pct / 100), 0);
  const total = subtotal + gstTotal;
  const itemsCount = Object.values(cart).filter(v => v > 0).length;

  const cartItems = Object.entries(cart).filter(([, q]) => q > 0).map(([product_id, qty_pcs]) => ({ product_id, qty_pcs }));

  const openConfirm = () => {
    if (cartItems.length === 0) return toast.error("Add at least one product");
    setConfirmOpen(true);
  };

  const doPlace = async () => {
    setBusy(true);
    try {
      const payloadItems = cartItems.map(ci => {
        const p = products.find(x => x.id === ci.product_id);
        const boxQty = p?.box_qty || 1;
        return { product_id: ci.product_id, qty_boxes: Math.round((ci.qty_pcs / boxQty) * 100) / 100 };
      });
      const o = await dms.placeOrder({ items: payloadItems, notes });
      toast.success(`Order ${o.order_no} placed!`);
      setConfirmOpen(false);
      nav(`/dms/distributor/my-orders/${o.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  // Group by category with counts & totals
  const grouped = {};
  products.forEach(p => { (grouped[p.category_name || "Uncategorised"] ||= []).push(p); });
  const categoryList = Object.entries(grouped).map(([name, prods]) => {
    const inCart = prods.reduce((n, p) => n + (cart[p.id] > 0 ? 1 : 0), 0);
    return { name, count: prods.length, inCart };
  });

  return (
    <div className="pb-32">
      <PageHeader
        title={openCategory ? openCategory : "Browse & Order"}
        subtitle={openCategory ? "Tap + / − to set quantity per product" : "Pick a category to see products"}
        back={openCategory ? undefined : undefined}
        action={openCategory && (
          <Button variant="outline" onClick={() => setOpenCategory(null)} data-testid="back-to-cats"><ChevronRight className="rotate-180 mr-1" size={16} /> All Categories</Button>
        )}
      />

      {/* STEP 1 — category grid */}
      {!openCategory && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categoryList.length === 0 && <EmptyState icon={Package} title="No products available" description="Owner hasn't given you visibility yet." />}
          {categoryList.map(c => (
            <button
              key={c.name}
              onClick={() => setOpenCategory(c.name)}
              className="text-left bg-white border border-slate-200 hover:border-[#c9a227] hover:shadow-md transition rounded-2xl p-5 relative"
              data-testid={`cat-tile-${c.name}`}
            >
              <div className="h-10 w-10 rounded-xl bg-[#faf6e6] text-[#a67c00] flex items-center justify-center mb-3"><Package size={20} /></div>
              <div className="font-semibold text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{c.count} product{c.count !== 1 ? "s" : ""}</div>
              {c.inCart > 0 && (
                <span className="absolute top-3 right-3 bg-[#c9a227] text-white text-[10px] font-bold rounded-full px-2 py-0.5">{c.inCart} in cart</span>
              )}
              <div className="mt-4 text-[#a67c00] text-xs font-medium flex items-center">Open <ChevronRight size={12} className="ml-0.5" /></div>
            </button>
          ))}
        </div>
      )}

      {/* STEP 2 — product list inside category */}
      {openCategory && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(grouped[openCategory] || []).map(p => {
            const q = cart[p.id] || 0;
            const priceChanged = p.previous_price && p.previous_price !== p.unit_price;
            return (
              <Card key={p.id} className="p-4 flex flex-col" data-testid={`product-card-${p.id}`}>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-semibold text-slate-900 leading-tight">{p.name}</div>
                    <div className="flex items-center gap-1 shrink-0">
                      {priceChanged && <span className="text-[10px] uppercase font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Price ↑</span>}
                      <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                        <Package size={10} /> {p.box_qty}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs font-mono text-slate-500 mb-2">{p.sku_code} • {p.box_qty} bottles/box</div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-lg font-bold text-[#a67c00]">{inr(Math.round(p.unit_price))}</span>
                    <span className="text-xs text-slate-500">/ nos (excl. GST)</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">+{p.gst_pct}% GST</div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setQty(p.id, -1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center" data-testid={`minus-${p.id}`}><Minus size={16} /></button>
                    <input type="number" min={0} value={q} onChange={e => setQtyDirect(p.id, e.target.value)} className="w-14 h-9 text-center border border-slate-200 rounded-lg text-sm font-semibold" data-testid={`qty-input-${p.id}`} />
                    <button onClick={() => setQty(p.id, 1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center bg-[#faf6e6] text-[#a67c00]" data-testid={`plus-${p.id}`}><Plus size={16} /></button>
                  </div>
                  {q > 0 && p.box_qty > 0 && (
                    <div className="rounded-lg border border-[#e8dcae] bg-[#faf6e6] px-2 py-1 text-center leading-tight" data-testid={`boxprice-${p.id}`}>
                      <div className="text-[10px] text-slate-500">{p.box_qty} pcs/box</div>
                      <div className="text-sm font-bold text-[#a67c00]">{inr(Math.round(p.unit_price * (1 + (p.gst_pct || 0) / 100) * p.box_qty))}</div>
                    </div>
                  )}
                  <div className="text-right ml-auto">
                    {q > 0 && <div className="text-sm font-semibold text-slate-900">{inr(Math.round(q * p.unit_price))}</div>}
                    {q > 0 && <div className="text-[9px] text-slate-400">excl. GST</div>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Sticky cart footer */}
      <div className="fixed bottom-0 left-0 lg:left-60 right-0 bg-white border-t border-slate-200 shadow-lg z-40">
        <div className="p-4 flex items-center gap-4">
          <div className="flex-1">
            <div className="text-xs text-slate-500">{itemsCount} items • Subtotal {inr(subtotal)} + GST {inr(gstTotal)} <span className="ml-1 text-[10px] uppercase font-semibold text-[#a67c00]">using NEW price</span></div>
            <div className="text-xl font-bold text-slate-900">{inr(total)}</div>
          </div>
          <div className="hidden md:block flex-1 max-w-sm"><Input placeholder="Notes for owner (optional)" value={notes} onChange={e => setNotes(e.target.value)} /></div>
          <Button disabled={itemsCount === 0 || busy} onClick={openConfirm} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white h-11 px-6" data-testid="place-order-btn">
            <ShoppingCart size={16} className="mr-2" /> Place Order
          </Button>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Your Order</DialogTitle>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-2 my-2">
            {cartItems.map(ci => {
              const p = products.find(x => x.id === ci.product_id);
              if (!p) return null;
              return (
                <div key={ci.product_id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                  <div>
                    <div className="font-medium text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{ci.qty_pcs} pc{ci.qty_pcs !== 1 ? "s" : ""} × {inr(p.unit_price)}</div>
                  </div>
                  <div className="font-semibold text-slate-900">{inr(ci.qty_pcs * p.unit_price)}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-base font-bold border-t border-slate-200 pt-3">
            <span>Total</span>
            <span>{inr(total)}</span>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="w-full sm:w-auto" data-testid="confirm-edit-btn">Edit</Button>
            <Button variant="outline" onClick={() => { setCart({}); setConfirmOpen(false); }} className="w-full sm:w-auto text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="confirm-delete-btn">Delete</Button>
            <Button onClick={doPlace} disabled={busy} className="w-full sm:w-auto bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="confirm-place-btn">
              <ShoppingCart size={16} className="mr-2" /> Confirm & Place Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Distributor — My Primary Orders + Detail
// ============================================================================
export function DistributorOrdersPage() {
  const [list, setList] = useState([]);
  const nav = useNavigate();
  const load = () => dms.listOrders().then(d => setList(d.data));
  useEffect(() => { load(); }, []);

  const doDelete = async (e, o) => {
    e.stopPropagation();
    if (!window.confirm(`Delete order ${o.order_no}? This cannot be undone.`)) return;
    try {
      await dms.deleteOrder(o.id);
      toast.success(`${o.order_no} deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete order");
    }
  };

  return (
    <div>
      <PageHeader title="My Primary Orders" subtitle="Orders you've placed with the owner" />
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Order #</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Fulfillment</TableHead><TableHead>Status</TableHead><TableHead>Placed</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map(o => (
              <TableRow key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/dms/distributor/my-orders/${o.id}`)} data-testid={`my-ord-${o.id}`}>
                <TableCell className="font-mono text-sm">{o.order_no}{o.is_backorder && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 align-middle">Backorder</span>}</TableCell>
                <TableCell>{o.items.length}</TableCell>
                <TableCell className="font-medium">{inr(o.total)}</TableCell>
                <TableCell><div className="flex items-center gap-2 min-w-[100px]"><div className="w-16 h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-[#faf6e6]0" style={{ width: `${o.fulfillment_pct}%` }} /></div><span className="text-xs">{o.fulfillment_pct}%</span></div></TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded-full border ${statusPill(o.status)}`}>{o.status.replace(/_/g, " ")}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(o.created_at)}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); window.open(`/dms/print/purchase-order/${o.id}`, "_blank"); }}
                      className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center"
                      data-testid={`print-order-${o.id}`}
                      title="Print / Download PO"
                    >
                      <Printer size={14} />
                    </button>
                    {o.status === "pending" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); nav(`/dms/distributor/my-orders/${o.id}`); }}
                        className="h-8 w-8 rounded-lg border border-slate-200 text-[#a67c00] hover:bg-[#faf6e6] flex items-center justify-center"
                        data-testid={`edit-order-${o.id}`}
                        title="Edit order"
                      >
                        <Edit size={14} />
                      </button>
                    )}
                    {(o.status === "pending" || o.status === "ready_to_go") && (
                      <button
                        onClick={(e) => doDelete(e, o)}
                        className="h-8 w-8 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center"
                        data-testid={`delete-order-${o.id}`}
                        title="Delete order"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {list.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No orders yet</div>}
      </Card>
    </div>
  );
}

export function DistributorOrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editQty, setEditQty] = useState({});
  const [addedProducts, setAddedProducts] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [pickCategory, setPickCategory] = useState(null);
  const [pickQty, setPickQty] = useState({});
  const load = () => dms.getOrder(id).then(o => { setOrder(o); const q = {}; o.items.forEach(it => { q[it.product_id] = Math.round(it.qty_boxes_ordered * (it.box_qty || 1) * 100) / 100; }); setEditQty(q); setAddedProducts({}); });
  useEffect(() => { load(); }, [id]);

  const receive = async () => {
    if (!window.confirm("Confirm you have physically received this shipment?")) return;
    setBusy(true);
    try { await dms.markReceived(id); toast.success("Marked as Received"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const saveEdit = async () => {
    const items = Object.entries(editQty)
      .map(([product_id, pcs]) => {
        const it = order.items.find(x => x.product_id === product_id) || addedProducts[product_id];
        const boxQty = it?.box_qty || 1;
        return { product_id, qty_boxes: Math.round((Number(pcs) / boxQty) * 100) / 100 };
      })
      .filter(it => it.qty_boxes > 0);
    if (items.length === 0) return toast.error("At least one item with quantity > 0 required");
    setBusy(true);
    try { await dms.updatePrimaryOrder(id, { items }); toast.success("Order updated"); setEditing(false); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to update order"); }
    setBusy(false);
  };

  const openPicker = () => {
    setPickCategory(null);
    setPickerOpen(true);
    if (allProducts.length === 0) dms.browseProducts().then(d => setAllProducts(d.data || [])).catch(() => toast.error("Could not load products"));
  };
  const removeRow = (pid) => {
    const n = { ...editQty }; delete n[pid]; setEditQty(n);
    const a = { ...addedProducts }; delete a[pid]; setAddedProducts(a);
  };
  const addProduct = (pr) => {
    const pcs = Number(pickQty[pr.id]) || pr.box_qty || 1;
    setEditQty(prev => ({ ...prev, [pr.id]: pcs }));
    setAddedProducts(prev => ({ ...prev, [pr.id]: {
      product_id: pr.id, product_name: pr.name, sku_code: pr.sku_code, box_qty: pr.box_qty,
      unit_price: pr.unit_price, margin_pct: 0, qty_boxes_ordered: 0, qty_boxes_fulfilled: 0, line_total: 0,
    } }));
    toast.success(`${pr.name} added`);
  };
  const pickGrouped = {};
  allProducts.forEach(pr => { (pickGrouped[pr.category_name || "Uncategorised"] ||= []).push(pr); });

  if (!order) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  const rows = editing
    ? Object.keys(editQty).map(pid => order.items.find(x => x.product_id === pid) || addedProducts[pid]).filter(Boolean)
    : order.items;

  return (
    <div>
      <PageHeader
        title={order.order_no}
        subtitle={`Placed ${niceDate(order.created_at)}`}
        back="/dms/distributor/my-orders"
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(`/dms/print/purchase-order/${id}`, "_blank")} data-testid="dist-print-po"><Printer size={16} className="mr-1" /> Print PO</Button>
          {order.ebill && <Button variant="outline" onClick={() => window.open(`/dms/print/ebill/${order.ebill.id}`, "_blank")} data-testid="dist-print-ebill"><span className="mr-1">🖨</span> Print e-Bill</Button>}
          <ReturnRequestButton level="primary" order={order} onDone={load} />
          {order.status === "pending" && !editing && <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-order-btn"><Edit size={16} className="mr-1" /> Edit Order</Button>}
          {editing && <Button variant="outline" onClick={openPicker} data-testid="add-product-btn"><Plus size={16} className="mr-1" /> Add Product</Button>}
          {editing && <Button onClick={saveEdit} disabled={busy} className="bg-[#c9a227] hover:bg-[#a67c00] text-white" data-testid="save-order-edit-btn">Save Changes</Button>}
          {editing && <Button variant="outline" onClick={() => { setEditing(false); load(); }}>Cancel</Button>}
          {order.status === "ready_to_go" && <Button onClick={receive} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="mark-received-btn"><CheckCircle2 size={16} className="mr-1" /> Mark Received</Button>}
          {(order.status === "fulfilled" || order.status === "partially_fulfilled") && <Button variant="outline" disabled title="Available after the owner generates the invoice" data-testid="mark-received-waiting"><CheckCircle2 size={16} className="mr-1" /> Mark Received (after invoice)</Button>}
        </div>}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Status</div><div className="mt-1"><span className={`text-sm px-2.5 py-1 rounded-full border ${statusPill(order.status)}`}>{order.status.replace(/_/g, " ")}</span></div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Fulfillment</div><div className="mt-1 flex items-center gap-3"><div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-[#faf6e6]0 transition-all" style={{ width: `${order.fulfillment_pct}%` }} /></div><span className="font-bold">{order.fulfillment_pct}%</span></div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Total</div><div className="mt-1 font-bold text-lg">{inr(order.total)}</div></Card>
      </div>
      <Card className="mb-4">
        <div className="p-4 border-b border-slate-100 font-semibold">Items</div>
        <Table>
          <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Price</TableHead><TableHead>Ordered</TableHead><TableHead>Being Delivered</TableHead><TableHead>Total</TableHead>{editing && <TableHead></TableHead>}</TableRow></TableHeader>
          <TableBody>
            {rows.map(it => (
              <TableRow key={it.product_id}>
                <TableCell><div className="font-medium">{it.product_name}</div><div className="text-xs font-mono text-slate-500">{it.sku_code}</div></TableCell>
                <TableCell>
                  {it.margin_pct > 0 ? (
                    <>
                      <div className="text-xs text-slate-400 line-through">{inr(it.list_price)}</div>
                      <div>{inr(it.display_price != null ? it.display_price : (it.list_price - (it.list_price * it.margin_pct / 100)))}</div>
                      <div className="text-xs text-emerald-600">-{it.margin_pct}%</div>
                    </>
                  ) : (
                    <div>{inr(it.unit_price)}</div>
                  )}
                </TableCell>
                <TableCell>
                  {editing ? (
                    <Input type="number" min="0" className="w-24" value={editQty[it.product_id] ?? Math.round(it.qty_boxes_ordered * (it.box_qty || 1) * 100) / 100}
                      onChange={e => setEditQty({ ...editQty, [it.product_id]: e.target.value })} />
                  ) : (
                    <>{Math.round(it.qty_boxes_ordered * (it.box_qty || 1) * 100) / 100} pcs</>
                  )}
                </TableCell>
                <TableCell><span className="font-semibold">{Math.round(it.qty_boxes_fulfilled * (it.box_qty || 1) * 100) / 100} pcs</span> {it.qty_boxes_fulfilled < it.qty_boxes_ordered && <span className="text-xs text-amber-700 ml-1">(partial)</span>}</TableCell>
                <TableCell className="font-medium">{inr(it.line_total)}</TableCell>
                {editing && <TableCell><Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeRow(it.product_id)} data-testid={`remove-${it.product_id}`}>✕ Remove</Button></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{pickCategory ? pickCategory : "Add Product — select a category"}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {!pickCategory ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(pickGrouped).map(([name, prods]) => (
                  <button key={name} onClick={() => setPickCategory(name)} className="text-left bg-white border border-slate-200 hover:border-[#c9a227] rounded-xl p-4" data-testid={`pick-cat-${name}`}>
                    <div className="font-semibold text-slate-900">{name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{prods.length} product{prods.length !== 1 ? "s" : ""}</div>
                  </button>
                ))}
                {allProducts.length === 0 && <div className="col-span-full text-sm text-slate-500 p-4">Loading products…</div>}
              </div>
            ) : (
              <div className="space-y-2">
                {(pickGrouped[pickCategory] || []).map(pr => {
                  const already = editQty[pr.id] !== undefined;
                  return (
                    <div key={pr.id} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3">
                      <div className="flex-1">
                        <div className="font-medium leading-tight">{pr.name}</div>
                        <div className="text-xs font-mono text-slate-500">{pr.sku_code} • {pr.box_qty} pcs/box • {inr(Math.round(pr.unit_price))} / nos</div>
                      </div>
                      {already ? (
                        <span className="text-xs text-emerald-700 font-semibold">In order</span>
                      ) : (
                        <>
                          <Input type="number" min="1" className="w-20" placeholder={String(pr.box_qty || 1)} value={pickQty[pr.id] ?? ""} onChange={e => setPickQty({ ...pickQty, [pr.id]: e.target.value })} />
                          <Button size="sm" onClick={() => addProduct(pr)} data-testid={`pick-add-${pr.id}`}>Add</Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex justify-between pt-2">
            {pickCategory ? <Button variant="outline" onClick={() => setPickCategory(null)}>← All Categories</Button> : <span />}
            <Button onClick={() => setPickerOpen(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
      {order.ebill && (
        <Card className="mb-4 p-4">
          <div className="flex items-center gap-2 mb-2"><Receipt size={18} className="text-[#a67c00]" /><div className="font-semibold">e-Bill Generated</div></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-slate-500">Bill #</div><div className="font-mono">{order.ebill.ebill_no}</div></div>
            <div><div className="text-slate-500">Subtotal</div><div>{inr(order.ebill.subtotal)}</div></div>
            <div><div className="text-slate-500">GST</div><div>{inr(order.ebill.gst_total)}</div></div>
            {(order.ebill.trade_discount_total || 0) > 0 && <div><div className="text-slate-500">Trade Discount</div><div>- {inr(order.ebill.trade_discount_total)}</div></div>}
            <div><div className="text-slate-500">Total</div><div className="font-bold">{inr(order.ebill.total)}</div></div>
          </div>
          {(order.attachments || []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-xs text-slate-500 mb-2">Attachments:</div>
              {order.attachments.map(a => (
                <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#a67c00] hover:underline"><Paperclip size={14} /> {a.name}</a>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Distributor — My Stock (received inventory)
// ============================================================================
export function DistributorStockPage() {
  const [stock, setStock] = useState({ data: [], total_boxes: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openRows, setOpenRows] = useState({});
  useEffect(() => {
    let active = true;
    const load = (showSpinner) => {
      if (showSpinner) setLoading(true);
      dms.distributorMyStock().then((d) => { if (active) setStock(d); }).finally(() => { if (active && showSpinner) setLoading(false); });
    };
    load(true);
    // Live-ish refresh: re-fetch every 15s, and immediately whenever the tab
    // regains focus (e.g. distributor switches back after owner edits stock),
    // so the header total never sits stale while the page stays open.
    const interval = setInterval(() => load(false), 15000);
    const onFocus = () => load(false);
    window.addEventListener("focus", onFocus);
    return () => { active = false; clearInterval(interval); window.removeEventListener("focus", onFocus); };
  }, []);
  // Point 69: headline shows total pieces (same unit as the dashboard "Current Stock" KPI)
  const totalPieces = (stock.data || []).reduce((sum, it) => sum + (Number(it.no_of_pieces) || 0), 0);
  // Point 72: search by product name or SKU
  const filteredStock = (stock.data || []).filter((it) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (it.product_name || "").toLowerCase().includes(needle)
      || (it.sku_code || "").toLowerCase().includes(needle);
  });
  const stockTotals = filteredStock.reduce((acc, it) => {
    acc.qty_boxes += Number(it.qty_boxes) || 0;
    acc.no_of_pieces += Number(it.no_of_pieces) || 0;
    acc.no_of_liters += Number(it.no_of_liters) || 0;
    acc.line_value += Number(it.line_value) || 0;
    return acc;
  }, { qty_boxes: 0, no_of_pieces: 0, no_of_liters: 0, line_value: 0 });
  return (
    <div>
      <PageHeader title="My Stock" subtitle="Received inventory" />
      <Card className="p-6 text-center mb-4">
        <Warehouse size={32} className="mx-auto text-slate-400 mb-2" />
        <div className="text-lg font-semibold text-slate-900">{totalPieces.toLocaleString("en-IN")} pcs</div>
        <div className="text-sm text-slate-500">Total stock value {inr(stock.total_value || 0)}</div>
        <div className="text-sm text-slate-500">Actual purchase value (batch-wise, incl. GST) {inr(stock.total_actual_value || 0)}</div>
      </Card>
      <div className="mb-3">
        <Input placeholder="Search product name or SKU…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dist-stock-search-input" />
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-slate-500 text-xs uppercase">
              <th className="p-3">Product</th>
              <th className="p-3 text-right">Qty (Boxes)</th>
              <th className="p-3 text-right">Unit</th>
              <th className="p-3 text-right">No. of Pieces</th>
              <th className="p-3 text-right">No. of Liters</th>
              <th className="p-3 text-right">Rate</th>
              <th className="p-3 text-right">GST</th>
              <th className="p-3 text-right">Purchase Price</th>
              <th className="p-3 text-right">Sales Price</th>
              <th className="p-3 text-right">Margin</th>
              <th className="p-3 text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            {filteredStock.map((it) => {
              const bl = it.batches || [];
              const isOpen = openRows[it.product_id] !== undefined ? !!openRows[it.product_id] : bl.length > 1;
              return (
              <React.Fragment key={it.product_id}>
              <tr className="border-t border-slate-100">
                <td className="p-3">
                  <div className="flex items-start gap-2">
                    {bl.length > 0 ? (
                      <button type="button" className="mt-0.5 text-slate-500 hover:text-slate-900"
                        onClick={() => setOpenRows((m) => ({ ...m, [it.product_id]: !isOpen }))}
                        data-testid={"stock-expand-" + it.product_id}>
                        <ChevronRight className={"h-4 w-4 transition-transform " + (isOpen ? "rotate-90" : "")} />
                      </button>
                    ) : <span className="inline-block w-4" />}
                    <div>
                      <div className="font-medium text-slate-900">{it.product_name}</div>
                      {it.sku_code && <div className="font-mono text-[10px] text-slate-500">{it.sku_code}</div>}
                      {bl.length > 0 && <div className="text-[10px] text-slate-500">{bl.length} batch{bl.length > 1 ? "es" : ""}</div>}
                    </div>
                  </div>
                </td>
                <td className="p-3 text-right">{it.qty_boxes}</td>
                <td className="p-3 text-right">{it.unit}</td>
                <td className="p-3 text-right">{it.no_of_pieces}</td>
                <td className="p-3 text-right">{it.no_of_liters}</td>
                <td className="p-3 text-right">{inr(it.rate)}</td>
                <td className="p-3 text-right">{it.gst_rate_display}</td>
                <td className="p-3 text-right">{inr(it.purchase_price)}</td>
                <td className="p-3 text-right">{inr(it.sales_price)}</td>
                <td className="p-3 text-right">{it.margin_pct}%</td>
                <td className="p-3 text-right font-semibold">{inr(it.line_value)}</td>
              </tr>
              {isOpen && bl.length > 0 && (
                <tr className="bg-slate-50">
                  <td colSpan={11} className="px-6 py-3">
                    <div className="text-xs font-semibold text-slate-600 mb-2">Batch-wise stock (actual purchase price)</div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-slate-500 uppercase">
                          <th className="py-1 pr-3">Batch</th>
                          <th className="py-1 pr-3 text-right">Qty (Boxes)</th>
                          <th className="py-1 pr-3 text-right">Pieces</th>
                          <th className="py-1 pr-3 text-right">List price (incl GST)</th>
                          <th className="py-1 pr-3 text-right">Margin</th>
                          <th className="py-1 pr-3 text-right">Price / pc (ex GST)</th>
                          <th className="py-1 pr-3 text-right">Price / pc (incl GST)</th>
                          <th className="py-1 text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bl.map((b) => (
                          <tr key={it.product_id + "-" + b.batch_no} className="border-t border-slate-200">
                            <td className="py-1 pr-3">
                              <span className="font-medium text-slate-800">#{b.batch_no}</span>
                              <span className="text-slate-500"> {b.label}</span>{b.rate_mixed && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold">avg rate</span>}{(b.po_refs || []).length > 0 && <div className="text-[10px] text-slate-500">{(b.po_refs || []).slice(-4).map(r => r.po_no + " · " + String(r.at || "").slice(0, 10) + " · " + r.qty_boxes + " box @ " + (r.price_incl_gst != null ? inr(r.price_incl_gst) + " incl. GST" : inr(r.unit_price) + " ex-GST")).join(" | ")}</div>}
                            </td>
                            <td className="py-1 pr-3 text-right">{b.qty_boxes}</td>
                            <td className="py-1 pr-3 text-right">{b.no_of_pieces}</td>
                            <td className="py-1 pr-3 text-right">{inr(b.list_price)}</td>
                            <td className="py-1 pr-3 text-right">{b.margin_pct}%</td>
                            <td className="py-1 pr-3 text-right">{inr(b.purchase_price_ex_gst)}</td>
                            <td className="py-1 pr-3 text-right">{inr(b.purchase_price)}</td>
                            <td className="py-1 text-right font-semibold">{inr(b.line_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
            {!loading && filteredStock.length === 0 && (
              <tr><td colSpan={11} className="p-6 text-center text-slate-400">{q ? "No matching products" : "No stock received yet"}</td></tr>
            )}
          </tbody>
          {filteredStock.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold text-slate-900">
                <td className="p-3">Total</td>
                <td className="p-3 text-right">{stockTotals.qty_boxes.toLocaleString("en-IN")}</td>
                <td className="p-3"></td>
                <td className="p-3 text-right">{stockTotals.no_of_pieces.toLocaleString("en-IN")}</td>
                <td className="p-3 text-right">{stockTotals.no_of_liters.toLocaleString("en-IN")}</td>
                <td className="p-3"></td>
                <td className="p-3"></td>
                <td className="p-3"></td>
                <td className="p-3"></td>
                <td className="p-3"></td>
                <td className="p-3 text-right">{inr(stockTotals.line_value)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>

      <StockLedgerSection scope="distributor" />
    </div>
  );
}

// ============================================================================
// Distributor: Assigned Salespersons
// ============================================================================
// ============================================================================
// Distributor — Returns (my primary returns + secondary returns from retailers)
// ============================================================================
export function DistributorReturnsPage() {
  const [retDetail, setRetDetail] = useState(null);
  const [returns, setReturns] = useState([]);
  const [tab, setTab] = useState("primary");
  const [busy, setBusy] = useState(false);

  const load = () => dms.listReturns().then(d => setReturns(d.data || []));
  useEffect(() => { load(); }, []);

  const filtered = returns.filter(r => r.level === tab);

  const approve = async (id) => {
    if (!window.confirm("Approve this return? Stock will be adjusted.")) return;
    setBusy(true);
    try { await dms.approveReturn(id); toast.success("Return approved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (optional):") || "";
    setBusy(true);
    try { await dms.rejectReturn(id, reason); toast.success("Return rejected"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const statusPillReturn = (st) => st === "requested" ? "bg-amber-50 text-amber-700 border-amber-200" : st === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200";

  return (
    <div>
      <PageHeader title="Returns" subtitle="Primary returns you raised, and secondary returns from retailers" />
      <ReturnDetailDialog entry={retDetail} onClose={() => setRetDetail(null)} debtorView={tab === "primary"} />
      <div className="flex gap-2 mb-4">
        <Button size="sm" variant="outline" onClick={() => setTab("primary")} className={tab === "primary" ? "bg-[#a67c00] text-white border-[#a67c00] hover:bg-[#8a6600]" : ""}>Raised by Me</Button>
        <Button size="sm" variant="outline" onClick={() => setTab("secondary")} className={tab === "secondary" ? "bg-[#a67c00] text-white border-[#a67c00] hover:bg-[#8a6600]" : ""}>From Retailers</Button>
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={RotateCcw} title="No returns" description="No return requests here." />
      ) : (
        <Card>
          <Table>
            <TableHeader><TableRow><TableHead>Return #</TableHead>{tab === "secondary" && <TableHead>Retailer</TableHead>}<TableHead>Order</TableHead><TableHead>Items</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead>{tab === "secondary" && <TableHead></TableHead>}</TableRow></TableHeader>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.id} data-testid="dist-return-row" onClick={() => setRetDetail({ reference_id: r.id, reference_no: r.return_no })} className="cursor-pointer hover:bg-slate-50">
                  <TableCell className="font-mono">{r.return_no}</TableCell>
                  {tab === "secondary" && <TableCell>{r.retailer_id}</TableCell>}
                  <TableCell>{r.order_no || "-"}</TableCell>
                  <TableCell>{r.items.length} item(s)</TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded-full border ${statusPillReturn(r.status)}`}>{r.status}</span></TableCell>
                  <TableCell>{niceDate(r.created_at)}</TableCell>
                  {tab === "secondary" && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {r.status === "requested" && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => approve(r.id)} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white">Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => reject(r.id)} disabled={busy} className="text-red-600 border-red-200">Reject</Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

export function DistributorSalespersonsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    dms.distributorSalespersons().then(d => setRows(d.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <div>
      <PageHeader title="Assigned Salespersons" subtitle="Salespersons working for your distribution" back="/dms/distributor" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Phone</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={2} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={2} className="text-center py-8 text-slate-400">No salespersons assigned yet</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-slate-500">{r.phone || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============================================================================
// Distributor: Stock Alerts
// ============================================================================
export function DistributorStockAlertsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    dms.distributorStockAlerts().then(d => setRows(d.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <div>
      <PageHeader title="Stock Alerts" subtitle="Products running low (10 boxes or fewer)" back="/dms/distributor" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Product</TableHead>
            <TableHead>SKU</TableHead>
            <TableHead className="text-right">Qty (Boxes)</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-slate-400">No low-stock products — all good</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.product_id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{r.sku_code || "—"}</TableCell>
                <TableCell className="text-right font-semibold text-rose-700">{r.qty_boxes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============================================================================
// Distributor - Sale in Liters
// ============================================================================
export function DistributorSaleLitersPage() {
  const _n = new Date();
  const _pad = (x) => String(x).padStart(2, "0");
  const _ms = `${_n.getFullYear()}-${_pad(_n.getMonth() + 1)}-01`;
  const _me = `${_n.getFullYear()}-${_pad(_n.getMonth() + 1)}-${_pad(new Date(_n.getFullYear(), _n.getMonth() + 1, 0).getDate())}`;
  const base = { date_from: _ms, date_to: _me, salesperson_id: "", category: "", status: "sold", retailer_id: "" };
  const [f, setF] = useState(base);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sps, setSps] = useState([]);
  const [cats, setCats] = useState([]);
  const [rets, setRets] = useState([]);
  const load = (flt) => {
    setLoading(true);
    const params = {};
    Object.keys(flt).forEach(k => { if (flt[k]) params[k] = flt[k]; });
    dms.distSaleLiters(params).then(setData).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  useEffect(() => {
    load(base);
    dms.distSalespersonsSelf().then(d => setSps(Array.isArray(d) ? d : (d.data || d.salespersons || []))).catch(() => {});
    dms.listCategories().then(d => setCats(Array.isArray(d) ? d : (d.data || []))).catch(() => {});
    dms.listRetailers({ lite: 1 }).then(d => setRets(d.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const sel = "h-10 rounded-md border border-slate-200 bg-white px-3 text-sm";
  const fmt = (x) => Number(x || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const rows = data?.rows || [];
  return (
    <div>
      <PageHeader title="Sale in Liters" subtitle="Item-wise liters sold, filter by date, salesperson, category, status and retailer" />
      <Card className="p-3 mb-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[11px] text-slate-500 mb-1">From</div>
            <input type="date" className={sel} value={f.date_from} onChange={e => set("date_from", e.target.value)} data-testid="sl-date-from" />
          </div>
          <div>
            <div className="text-[11px] text-slate-500 mb-1">To</div>
            <input type="date" className={sel} value={f.date_to} onChange={e => set("date_to", e.target.value)} data-testid="sl-date-to" />
          </div>
          <select className={sel} value={f.salesperson_id} onChange={e => set("salesperson_id", e.target.value)} data-testid="sl-salesperson">
            <option value="">All salespersons</option>
            {sps.map(s => <option key={s.id} value={s.id}>{s.name || s.email || s.id}</option>)}
          </select>
          <select className={sel} value={f.category} onChange={e => set("category", e.target.value)} data-testid="sl-category">
            <option value="">All categories</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className={sel} value={f.status} onChange={e => set("status", e.target.value)} data-testid="sl-status">
            <option value="sold">Sold (Invoiced + Dispatched)</option>
            <option value="dispatched">Dispatched</option>
            <option value="invoiced">Invoiced</option>
            <option value="pending">Pending</option>
            <option value="all">All (incl. Pending)</option>
          </select>
          <select className={sel} value={f.retailer_id} onChange={e => set("retailer_id", e.target.value)} data-testid="sl-retailer">
            <option value="">All retailers</option>
            {rets.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <Button onClick={() => load(f)} data-testid="sl-apply">Apply</Button>
          <Button variant="outline" onClick={() => { setF(base); load(base); }}>Reset</Button>
        </div>
      </Card>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Total Liters</div><div className="mt-1 text-2xl font-bold text-slate-900">{fmt(data?.total_liters)} L</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Total Pieces</div><div className="mt-1 text-2xl font-bold text-slate-900">{fmt(data?.total_pieces)}</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Orders</div><div className="mt-1 text-2xl font-bold text-slate-900">{data?.order_count ?? 0}</div></Card>
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>SKU</TableHead><TableHead>Item</TableHead><TableHead>Category</TableHead><TableHead>Pack</TableHead>
            <TableHead className="text-right">Pieces</TableHead><TableHead className="text-right">Liters</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">Loading...</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No sale found for these filters</TableCell></TableRow>}
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{r.sku_code || "-"}</TableCell>
                <TableCell className="font-medium">{r.product_name}</TableCell>
                <TableCell className="text-xs">{r.category}</TableCell>
                <TableCell className="text-xs">{r.pack_size || "-"}</TableCell>
                <TableCell className="text-right">{fmt(r.pieces)}</TableCell>
                <TableCell className="text-right font-semibold">{fmt(r.liters)}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length > 0 && (
              <TableRow className="bg-slate-50 font-bold">
                <TableCell colSpan={4}>Total</TableCell>
                <TableCell className="text-right">{fmt(data?.total_pieces)}</TableCell>
                <TableCell className="text-right">{fmt(data?.total_liters)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}


// ── Price List (distributor): month-wise lists assigned by owner ──
export function DistributorPriceListPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (cid) => dms.distributorPriceLists(cid).then(setData).catch(() => toast.error("Failed to load price list"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const makeDefault = async () => {
    setBusy(true);
    try { await dms.setDefaultPriceList(data.selected_circular_id); toast.success("Default price list updated"); await load(data.selected_circular_id); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (!data) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (!data.restricted || data.lists.length === 0) {
    return (
      <div>
        <PageHeader title="Price List" subtitle="Month-wise price lists" />
        <EmptyState icon={Package} title="No price list assigned" description="Owner has not assigned a price list to you yet." />
      </div>
    );
  }
  const isDefault = data.selected_circular_id === data.default_circular_id;
  return (
    <div>
      <PageHeader
        title="Price List"
        subtitle="Prices on this list are used for your orders when it is your default"
        action={(
          <div className="flex items-center gap-2">
            <select
              className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
              value={data.selected_circular_id || ""}
              onChange={e => load(e.target.value)}
              data-testid="pricelist-select"
            >
              {data.lists.map(l => (
                <option key={l.id} value={l.id}>{(l.title || l.batch_label || l.id) + " · " + (l.effective_date || "")}{l.id === data.default_circular_id ? " (default)" : ""}</option>
              ))}
            </select>
            <Button onClick={makeDefault} disabled={busy || isDefault} data-testid="pricelist-set-default">{isDefault ? "Default" : "Set as default"}</Button>
          </div>
        )}
      />
      <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Category</th><th className="text-left p-3">Product</th><th className="text-left p-3">SKU</th>
              <th className="text-right p-3">MRP</th><th className="text-right p-3">DLP</th><th className="text-right p-3">Margin %</th><th className="text-right p-3">Net price</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map(r => (
              <tr key={r.product_id} className="border-t border-slate-100">
                <td className="p-3">{r.category_name}</td>
                <td className="p-3">{r.product_name}</td>
                <td className="p-3 font-mono text-xs">{r.sku_code}</td>
                <td className="p-3 text-right">{inr(r.mrp)}</td>
                <td className="p-3 text-right">{inr(r.dlp)}</td>
                <td className="p-3 text-right">{r.margin_pct}</td>
                <td className="p-3 text-right font-semibold">{inr(r.net_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
